import React, { useEffect, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import classes from "./PaginationComp.module.css";

gsap.registerPlugin(ScrollToPlugin);

export default function PaginationComp({
  total,
  perPage,
  currentPage,
  onPageChange,
}) {
  const totalPages = Math.ceil(total / perPage);
  const safePage = Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1));

  useEffect(() => {
    gsap.killTweensOf(window);
    gsap.to(window, {
      duration: 0.7,
      ease: "power2.out",
      scrollTo: { y: 0, autoKill: false },
    });
  }, [safePage]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 4) {
      return [...Array(totalPages).keys()].map((i) => i + 1);
    }

    const items = [];

    // Always show first page
    items.push(1);

    // If current page > 3 → show left dots
    if (safePage > 3) {
      items.push("left-dot");
    }

    // Middle visible pages (only 3 max)
    let middleStart = Math.max(2, safePage - 1);
    let middleEnd = Math.min(totalPages - 1, safePage + 1);

    for (let i = middleStart; i <= middleEnd; i++) {
      items.push(i);
    }

    // If current page is NOT near last page → show right dots
    if (safePage < totalPages - 2) {
      items.push("right-dot");
    }

    // Always show last page
    items.push(totalPages);

    return items;
  }, [totalPages, safePage]);

  if (totalPages <= 1) return null;

  const handleChange = (page) => {
    if (page === safePage || page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  return (
    <div className={classes.pagination}>
      <button
        className={classes.pageBtn}
        disabled={safePage === 1}
        onClick={() => handleChange(safePage - 1)}
      >
        Prev
      </button>

      {paginationItems.map((item, idx) =>
        typeof item === "string" ? (
          <span key={idx} className={classes.ellipsis}>
            …
          </span>
        ) : (
          <button
            key={idx}
            className={`${classes.pageBtn} ${
              safePage === item ? classes.active : ""
            }`}
            onClick={() => handleChange(item)}
          >
            {item}
          </button>
        )
      )}

      <button
        className={classes.pageBtn}
        disabled={safePage === totalPages}
        onClick={() => handleChange(safePage + 1)}
      >
        Next
      </button>
    </div>
  );
}
